import type { KioskTransaction, KioskTransactionDetail } from './types';

export const REFUND_STATE_FILTERS = [
  'open',
  'paid',
  'expired_no_attempt',
  'expired_attempt_failed',
  'expired_result_unknown',
  'expired_reader_replaced',
  'paid_after_expiry',
  'refunded_by_hand',
  'released',
  'captured',
  'refund_pending',
  'refunded',
  'unknown',
  'failed',
] as const;

export const KIOSK_PAYMENT_METHODS = ['octopus', 'card', 'qr', 'free'] as const;

export const KIOSK_TRANSACTION_FILTER_KEYS = ['siteId', 'method', 'refundState', 'receiptReference', 'cardHash', 'from', 'to'] as const;

export const SOEPAY_METHODS = ['card', 'qr'] as const;

export const MARK_REFUSAL_STATES = ['paid', 'refunded_by_hand', 'open', 'none', 'soepay_in_progress', 'soepay_unchecked'] as const;
export type MarkRefusalState = (typeof MARK_REFUSAL_STATES)[number];

export const PROVIDER_REFUSAL_REASONS = ['no_soepay_instruction', 'nothing_to_check', 'refunded_by_hand', 'not_proven_undone', 'state_changed'] as const;

export function isSoepayMethod(method: string): boolean {
  return (SOEPAY_METHODS as readonly string[]).includes(method);
}

export function refundStateColor(state: string): 'default' | 'success' | 'warning' | 'error' | 'info' {
  switch (state) {
    case 'open':
    case 'refund_pending':
      return 'info';
    case 'paid':
    case 'refunded_by_hand':
    case 'released':
    case 'captured':
    case 'refunded':
      return 'success';
    case 'paid_after_expiry':
    case 'failed':
      return 'error';
    case 'none':
      return 'default';
    default:
      return 'warning';
  }
}

export function settleStateColor(state: KioskTransaction['settleState']): 'default' | 'success' | 'warning' | 'error' | 'info' {
  switch (state) {
    case 'done':
      return 'success';
    case 'pending':
      return 'info';
    case 'unknown':
      return 'warning';
    case 'failed':
      return 'error';
    case 'none':
      return 'default';
  }
}

type MarkFields = Pick<KioskTransaction, 'method' | 'status' | 'refundedByHand' | 'refundAmount' | 'csRefundStatus' | 'depositAmount' | 'settleType'>;

export function owedByHandAmount(record: MarkFields): number | null {
  if (record.refundedByHand) return null;
  if (record.method === 'octopus') return record.status === 'refund_expired' ? record.refundAmount : null;
  if (!isSoepayMethod(record.method) || record.status !== 'settle_failed' || record.csRefundStatus !== 'pending') return null;
  return record.refundAmount;
}

export function canMarkRefundedByHand(record: MarkFields): boolean {
  return owedByHandAmount(record) !== null;
}

export function canCheckWithProvider(record: Pick<KioskTransactionDetail, 'settleInstruction'>): boolean {
  return record.settleInstruction !== null && (record.settleInstruction.status === 'in_progress' || record.settleInstruction.status === 'failed');
}

export function markRefusalKey(state: unknown, method: string): string | null {
  if (typeof state !== 'string' || !(MARK_REFUSAL_STATES as readonly string[]).includes(state)) return null;
  if (state === 'open' || state === 'none') return `markRefusals.${state}_${isSoepayMethod(method) ? 'soepay' : 'octopus'}`;
  return `markRefusals.${state}`;
}

export function providerRefusalKey(reason: unknown): string {
  if (typeof reason === 'string' && (PROVIDER_REFUSAL_REASONS as readonly string[]).includes(reason)) return `providerActions.refusals.${reason}`;
  return 'errors.KIOSK_PROVIDER_ACTION_REFUSED';
}

const reportedMissingDetail = new Set<string>();

export function paidWith(record: Pick<KioskTransaction, 'id' | 'method' | 'cardBrand' | 'cardLast4' | 'qrWallet'>, translate: (key: string) => string): string | null {
  if (!isSoepayMethod(record.method)) return null;
  if (record.method === 'card' && record.cardBrand !== null && record.cardLast4 !== null) return `${record.cardBrand} ${record.cardLast4}`;
  if (record.method === 'qr' && record.qrWallet !== null) return translate(`resources.kiosk-transactions.wallets.${record.qrWallet}`);
  if (!reportedMissingDetail.has(record.id)) {
    reportedMissingDetail.add(record.id);
    console.error(`kiosk payment ${record.id} is a ${record.method} payment without its card or wallet detail`);
  }
  return translate('resources.kiosk-transactions.detailsMissing');
}

export const KNOWN_VALUE_LABELS = {
  settleTypes: ['card_capture', 'card_void', 'card_refund', 'qr_refund', 'qr_cancel'],
  instructionStatuses: ['pending', 'in_progress', 'completed', 'failed', 'cancelled', 'expired'],
  sentCalls: ['capture', 'cancel', 'refund'],
  lastChecks: ['done', 'not_done', 'unclear', 'conflict'],
  failureReasons: ['maav_exceeded', 'card_not_accepted', 'reader_error', 'unknown_outcome', 'provider_refused', 'provider_unclear', 'conflict'],
} as const;

export function valueLabel(translate: (key: string) => string, group: string, value: string): string {
  const known: readonly string[] | undefined = (KNOWN_VALUE_LABELS as Record<string, readonly string[]>)[group];
  if (known === undefined || !known.includes(value)) return translate('resources.kiosk-transactions.unknownValue');
  return translate(`resources.kiosk-transactions.${group}.${value}`);
}

export function exportQuery(filter: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const key of KIOSK_TRANSACTION_FILTER_KEYS) {
    const value = filter[key];
    if (typeof value === 'string' && value.trim() !== '') params.set(key, value.trim());
  }
  const query = params.toString();
  return query === '' ? '' : `?${query}`;
}
